// src/pages/AdminProducts.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/axios";
import { useI18n } from "../../i18n/I18nContext";

function SkeletonRow({ cols = 5 }) {
  return (
    <tr className="border-t">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="p-3">
          <div className="h-4 w-full max-w-[220px] animate-pulse rounded bg-gray-200" />
        </td>
      ))}
    </tr>
  );
}

function StatusBadge({ active }) {
  const { t } = useI18n();

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
        active
          ? "border-green-200 bg-green-50 text-green-700"
          : "border-gray-200 bg-gray-50 text-gray-700"
      }`}
    >
      {active ? t("Ativo") : t("Inativo")}
    </span>
  );
}

function normalizePaginated(resData) {
  return resData?.data?.data || resData?.data || [];
}

export default function AdminProducts() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const productsCountLabel = useMemo(() => {
    if (loading) return t("A carregar…");
    return `${products.length} produto${products.length === 1 ? "" : "s"}`;
  }, [products.length, loading, t]);

  const loadData = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await api.get("/api/admin/products");
      setProducts(normalizePaginated(res));
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro a carregar produtos."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const deleteProduct = async (id) => {
    const p = products.find((x) => Number(x.id) === Number(id));
    const label = p ? `${p.name} (${p.sku || t("sem SKU")})` : `ID ${id}`;

    if (!window.confirm(`${t("Queres mesmo apagar o produto")} ${label}?`)) return;

    setErrorMsg("");

    try {
      await api.delete(`/api/admin/products/${id}`);
      await loadData();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro a apagar produto."));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 px-6 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("Produtos (Admin)")}</h1>
            <p className="mt-1 text-sm text-gray-600">{productsCountLabel}</p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={loadData}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
              disabled={loading}
            >
              ⟳ {t("Recarregar")}
            </button>

            <button
              onClick={() => navigate("/admin/products/create")}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
            >
              + {t("Novo produto")}
            </button>
          </div>
        </div>

        {errorMsg && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">{t("Lista de produtos")}</span>
              <span className="text-xs text-gray-500">
                {loading ? t("A obter dados…") : t("Atualizado")}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="p-3">ID</th>
                  <th className="p-3">{t("Nome")}</th>
                  <th className="p-3">SKU</th>
                  <th className="p-3">{t("Estado")}</th>
                  <th className="p-3">{t("Imagem")}</th>
                  <th className="p-3 text-right">{t("Ações")}</th>
                </tr>
              </thead>

              <tbody className="text-gray-800">
                {loading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <SkeletonRow key={`sk-${i}`} cols={6} />
                  ))
                ) : products.length ? (
                  products.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-gray-50/60">
                      <td className="p-3 font-medium text-gray-900">{p.id}</td>
                      <td className="p-3">{p.name}</td>
                      <td className="p-3 text-gray-700">{p.sku || "—"}</td>
                      <td className="p-3">
                        <StatusBadge active={!!p.is_active} />
                      </td>
                      <td className="p-3">
                        {p.image ? (
                          <span className="text-xs text-gray-700">{t("Sim")}</span>
                        ) : (
                          <span className="text-xs text-gray-500">{t("Não")}</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/products/${p.id}/edit`)}
                            className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800"
                          >
                            {t("Editar")}
                          </button>
                          <button
                            onClick={() => deleteProduct(p.id)}
                            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                          >
                            {t("Apagar")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-10 text-center" colSpan={6}>
                      <div className="mx-auto max-w-md">
                        <div className="text-lg font-semibold text-gray-900">{t("Sem produtos")}</div>
                        <div className="mt-1 text-sm text-gray-600">
                          {t("Ainda não há registos para mostrar.")}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}